import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { AgentRefreshService } from '../../../agent/agent-refresh.service';
import {
  AlTableColumn,
  AlTableGlobalAction,
  AlTableRowAction,
} from '../../../components/features/table/table.component';
import { ReferralAgreementDialogComponent } from '../../../components/dialogs/referral-agreement-dialog/referral-agreement-dialog.component';
import { ReferralPartnerDialogComponent } from '../../../components/dialogs/referral-partner-dialog/referral-partner-dialog.component';
import { ConfirmDialogComponent } from '../../../components/dialogs/confirm-dialog/confirm-dialog.component';
import {
  CommissionStatus,
  ConnectStatus,
  EligibleClient,
  PayoutPreview,
  ReferralStatement,
  ReferralAgreement,
  ReferralCommission,
  ReferralPartner,
  ReferralService,
  VatMode,
} from '../../../services/referral.service';

/**
 * Partner revenue share (super_admin): who brought which client, at which
 * tier list, and which commission rows are open / released / paid.
 * PRD: features/prd/referral-commissions.md
 */
@Component({
  selector: 'app-admin-referrals',
  templateUrl: './referrals.component.html',
  styleUrl: './referrals.component.scss',
  providers: [DatePipe],
})
export class ReferralsComponent implements OnInit {
  partners: ReferralPartner[] = [];
  agreements: ReferralAgreement[] = [];
  commissions: ReferralCommission[] = [];
  clients: EligibleClient[] = [];

  partnerFilter: string | null = null;
  statusFilter: CommissionStatus | null = null;
  payoutReference = '';
  loading = false;

  readonly vatModes: VatMode[] = ['regular', 'small_business', 'reverse_charge'];
  readonly statuses: CommissionStatus[] = ['open', 'released', 'paid'];

  statements: ReferralStatement[] = [];
  /** Per partner id: Stripe Connect state, next statement preview, busy flag, onboarding link. */
  connect: Record<string, ConnectStatus | undefined> = {};
  previews: Record<string, PayoutPreview | undefined> = {};
  running: Record<string, boolean> = {};
  onboardingUrls: Record<string, string | undefined> = {};
  onboardingBusy: Record<string, boolean> = {};
  mailBusy: Record<string, boolean> = {};
  statementColumns: AlTableColumn[] = [];
  statementRowActions: AlTableRowAction[] = [];

  /** Sums of the rows currently shown, per status (net commission, cents). */
  sums = { open: 0, released: 0, paid: 0 };

  // Assigned once — app-table rebuilds on every new array (CLAUDE.md rule 18).
  agreementColumns: AlTableColumn[] = [];
  agreementRowActions: AlTableRowAction[] = [];
  agreementGlobalActions: AlTableGlobalAction[] = [];
  commissionColumns: AlTableColumn[] = [];
  commissionRowActions: AlTableRowAction[] = [];
  commissionGlobalActions: AlTableGlobalAction[] = [];

  constructor(
    private referralService: ReferralService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private agentRefresh: AgentRefreshService
  ) {
    // The portal assistant held or released a payout.
    this.agentRefresh
      .on('referrals')
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => this.reload());
  }

  ngOnInit(): void {
    const t = (k: string) => this.translate.instant('pages.admin.referrals.' + k);
    const eur = (cents: number) => this.eur(cents);
    const date = (v: string | null) => (v ? this.datePipe.transform(v, 'dd.MM.yyyy') || '' : '–');
    // Stripe errors carry a masked key (~100 stars, no space) and a URL — at full
    // length one word pushed the row menu out of the card. Discord keeps the whole text.
    const shortError = (e: string) => {
      const s = e.replace(/\*{3,}/g, '…');
      return s.length > 90 ? s.slice(0, 90).trimEnd() + '…' : s;
    };

    this.agreementColumns = [
      { key: 'client_name', kind: 'name', title: t('col.client') },
      { key: 'partner_name', title: t('col.partner'), width: '110px' },
      { key: 'start_date', kind: 'date', title: t('col.start'), width: '100px', map: date },
      { key: 'end_date', kind: 'date', title: t('col.end'), width: '100px', map: date },
      {
        key: 'current_month', kind: 'number', title: t('col.month'), width: '90px',
        map: (v: number | null) => (v ? String(v) : '–'),
      },
      {
        key: 'current_percent', kind: 'number', title: t('col.percent'), width: '80px',
        map: (v: number | null) => (v === null || v === undefined ? '–' : `${String(v).replace('.', ',')} %`),
      },
      {
        key: 'client_subscription_status', title: t('col.subscription'), width: '100px',
        map: (v: string | null) => v || '–',
      },
      { key: 'open', kind: 'number', title: t('col.openNet'), width: '100px', map: (_: any, r: ReferralAgreement) => eur(r.totals.open) },
      { key: 'released', kind: 'number', title: t('col.releasedNet'), width: '100px', map: (_: any, r: ReferralAgreement) => eur(r.totals.released) },
      { key: 'paid', kind: 'number', title: t('col.paidNet'), width: '100px', map: (_: any, r: ReferralAgreement) => eur(r.totals.paid) },
    ];
    this.agreementRowActions = [
      { key: 'edit', title: t('action.edit'), icon: 'edit-outline' },
      { key: 'sync', title: t('action.sync'), icon: 'refresh-outline' },
      { key: 'end', title: t('action.end'), icon: 'stop-circle-outline', disabled: (r: ReferralAgreement) => !!r.end_date },
    ];
    this.agreementGlobalActions = [{ title: t('action.add'), icon: 'plus-outline' }];

    this.commissionColumns = [
      { key: 'paid_at', kind: 'date', title: t('col.paidAt'), width: '100px', map: date },
      { key: 'client_name', kind: 'name', title: t('col.client') },
      {
        key: 'invoice_number', kind: 'text', title: t('col.invoice'), width: '140px',
        map: (v: string | null, r: ReferralCommission) =>
          (r.source_type === 'credit_note' ? t('creditNote') + ' ' : '') + (v || r.stripe_invoice_id || ''),
      },
      { key: 'contract_month', kind: 'number', title: t('col.month'), width: '80px', map: (v: number | null) => (v ? String(v) : '–') },
      { key: 'base_cents', kind: 'number', title: t('col.base'), width: '110px', map: (v: number) => eur(v) },
      { key: 'percent', kind: 'number', title: t('col.percent'), width: '70px', map: (v: number) => `${String(v).replace('.', ',')} %` },
      { key: 'commission_cents', kind: 'number', title: t('col.commission'), width: '110px', map: (v: number) => eur(v) },
      { key: 'vat_cents', kind: 'number', title: t('col.vat'), width: '90px', map: (v: number) => eur(v) },
      { key: 'status', title: t('col.status'), width: '110px', map: (v: CommissionStatus) => t('status.' + v) },
      { key: 'payout_reference', kind: 'text', title: t('col.reference'), width: '130px', map: (v: string | null) => v || '' },
    ];
    this.commissionRowActions = [
      { key: 'release', title: t('action.release'), icon: 'checkmark-outline', isMultiAction: true,
        disabled: (r: ReferralCommission) => r.status !== 'open' },
      { key: 'unrelease', title: t('action.unrelease'), icon: 'undo-outline', isMultiAction: true,
        disabled: (r: ReferralCommission) => r.status !== 'released' },
      { key: 'paid', title: t('action.markPaid'), icon: 'credit-card-outline', isMultiAction: true,
        disabled: (r: ReferralCommission) => r.status !== 'released' },
    ];
    this.commissionGlobalActions = [{ title: t('action.export'), icon: 'download-outline' }];

    this.statementColumns = [
      {
        key: 'number', kind: 'text', title: t('statements.number'), width: '130px',
        map: (v: string, r: ReferralStatement) => (r.source === 'alphacount' ? `${v} · alphacount` : v),
      },
      { key: 'partner_name', title: t('col.partner'), width: '110px' },
      { key: 'issued_on', kind: 'date', title: t('statements.issued'), width: '100px', map: date },
      {
        key: 'period_start', kind: 'date', title: t('statements.period'),
        map: (_: any, r: ReferralStatement) => `${date(r.period_start)} – ${date(r.period_end)}`,
      },
      { key: 'net_cents', kind: 'number', title: t('col.commission'), width: '110px', map: (v: number) => eur(v) },
      { key: 'vat_cents', kind: 'number', title: t('col.vat'), width: '100px', map: (v: number) => eur(v) },
      { key: 'gross_cents', kind: 'number', title: t('statements.payout'), width: '110px', map: (v: number) => eur(v) },
      {
        key: 'status', kind: 'text', title: t('col.status'), width: '140px',
        map: (v: string, r: ReferralStatement) => {
          let label = t('statements.status.' + v);
          if (v === 'issued' && r.payout_held_at) {
            label = t('statements.held');
          } else if (v === 'issued' && r.payout_due_at) {
            label = this.translate.instant('pages.admin.referrals.statements.dueAt', {
              when: this.datePipe.transform(r.payout_due_at, 'dd.MM.yyyy HH:mm') || '',
            });
          }
          return label + (r.error ? ` — ${shortError(r.error)}` : '');
        },
      },
      {
        key: 'sent_at', kind: 'text', title: t('statements.sentCol'), width: '170px',
        map: (v: string | null, r: ReferralStatement) => {
          if (r.source === 'alphacount') return v ? `${date(v)} · alphacount` : t('statements.viaAlphacount');
          const log = r.mail_log || [];
          if (!v || !log.length) return t('statements.notSent');
          const last = log[log.length - 1];
          return `${date(v)} · ${last.to}` + (log.length > 1 ? ` (${log.length}×)` : '');
        },
      },
    ];
    this.statementRowActions = [
      { key: 'pdf', title: t('statements.pdf'), icon: 'download-outline' },
      { key: 'pay', title: t('statements.pay'), icon: 'paper-plane-outline',
        disabled: (r: ReferralStatement) => r.status === 'paid' },
      { key: 'hold', title: t('statements.hold'), icon: 'pause-circle-outline',
        disabled: (r: ReferralStatement) => r.status !== 'issued' || !r.payout_due_at || !!r.payout_held_at },
      { key: 'release', title: t('statements.release'), icon: 'play-circle-outline',
        disabled: (r: ReferralStatement) => !r.payout_held_at || r.status === 'paid' },
      { key: 'send', title: t('statements.resend'), icon: 'email-outline',
        disabled: (r: ReferralStatement) => r.source === 'alphacount' },
    ];

    this.reload();
  }

  reload() {
    this.loading = true;
    this.referralService.listPartners().subscribe((p) => {
      this.partners = p;
      this.loadPartnerExtras();
    });
    this.referralService.listStatements().subscribe((s) => (this.statements = s));
    this.referralService.listAgreements().subscribe({
      next: (a) => (this.agreements = a),
      error: () => this.toastError(),
    });
    this.loadCommissions();
    this.referralService.listEligibleClients().subscribe((c) => (this.clients = c));
  }

  loadCommissions() {
    this.referralService
      .listCommissions({ partner_id: this.partnerFilter, status: this.statusFilter })
      .subscribe({
        next: (rows) => {
          this.commissions = rows;
          this.sums = { open: 0, released: 0, paid: 0 };
          for (const r of rows) this.sums[r.status] += r.commission_cents;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.toastError();
        },
      });
  }

  // ------------------------------------------------------------ partners

  private loadPartnerExtras() {
    for (const p of this.partners) {
      this.referralService.connectStatus(p.id).subscribe({
        next: (c) => (this.connect = { ...this.connect, [p.id]: c }),
        error: () => (this.connect = { ...this.connect, [p.id]: undefined }),
      });
      this.referralService.preview(p.id).subscribe((pv) => (this.previews = { ...this.previews, [p.id]: pv }));
    }
  }

  connectLabel(p: ReferralPartner): string {
    const c = this.connect[p.id];
    if (!c) return 'pages.admin.referrals.connect.unknown';
    if (c.transfers_active) return 'pages.admin.referrals.connect.active';
    if (c.connected) return 'pages.admin.referrals.connect.pending';
    return 'pages.admin.referrals.connect.none';
  }

  editPartner(p: ReferralPartner) {
    this.dialogService
      .open(ReferralPartnerDialogComponent, { context: { partner: p } })
      .onClose.subscribe((saved) => {
        if (saved) this.reload();
      });
  }

  createOnboardingLink(p: ReferralPartner) {
    this.onboardingBusy = { ...this.onboardingBusy, [p.id]: true };
    this.referralService.onboardingLink(p.id).subscribe({
      next: (r) => {
        this.onboardingBusy = { ...this.onboardingBusy, [p.id]: false };
        this.onboardingUrls = { ...this.onboardingUrls, [p.id]: r.url };
        this.referralService.connectStatus(p.id).subscribe((c) => (this.connect = { ...this.connect, [p.id]: c }));
      },
      error: (e) => {
        this.onboardingBusy = { ...this.onboardingBusy, [p.id]: false };
        this.toastError(e);
      },
    });
  }

  mailOnboardingLink(p: ReferralPartner) {
    this.mailBusy = { ...this.mailBusy, [p.id]: true };
    this.referralService.onboardingLink(p.id, true).subscribe({
      next: () => {
        this.mailBusy = { ...this.mailBusy, [p.id]: false };
        this.toastr.success(this.translate.instant('pages.admin.referrals.payout.mailed', { email: p.email }), '');
      },
      error: (e) => {
        this.mailBusy = { ...this.mailBusy, [p.id]: false };
        this.toastError(e);
      },
    });
  }

  openUrl(url: string) {
    window.open(url, '_blank', 'noopener');
  }

  copy(text: string) {
    navigator.clipboard?.writeText(text);
    this.toastr.success(this.translate.instant('pages.admin.referrals.payout.copied'), '');
  }

  async runPayout(p: ReferralPartner) {
    const pv = this.previews[p.id];
    const ok = await firstValueFrom(
      this.dialogService.open(ConfirmDialogComponent, {
        context: {
          title: 'pages.admin.referrals.runConfirm.title',
          message:
            p.billing_mode === 'self_billing'
              ? 'pages.admin.referrals.runConfirm.selfBilling'
              : 'pages.admin.referrals.runConfirm.partnerInvoice',
          details: this.translate.instant('pages.admin.referrals.payout.preview', {
            rows: pv?.rows ?? 0,
            net: this.eur(pv?.net_cents ?? 0),
            gross: this.eur(pv?.gross_cents ?? 0),
          }),
        },
      }).onClose
    );
    if (!ok) return;
    this.running = { ...this.running, [p.id]: true };
    this.referralService.runPayout(p.id).subscribe({
      next: (st) => {
        this.running = { ...this.running, [p.id]: false };
        this.toastr.success(this.translate.instant('pages.admin.referrals.payout.done', { number: st.number }), '');
        this.reload();
      },
      error: (e) => {
        this.running = { ...this.running, [p.id]: false };
        this.toastError(e);
        this.reload();
      },
    });
  }

  onStatementEvent(e: { event: string; data: any }) {
    if (e.event !== 'rowAction') return;
    const st = e.data.row as ReferralStatement;
    switch (e.data.action.key) {
      case 'pdf':
        this.referralService.statementPdf(st.id).subscribe({
          next: (blob) => this.download(blob, `${st.number}.pdf`),
          error: (err) => this.toastError(err),
        });
        break;
      case 'pay': {
        let ref: string | undefined;
        if (st.kind === 'partner_invoice' && !st.partner_invoice_ref) {
          ref = window.prompt(this.translate.instant('pages.admin.referrals.statements.invoiceRefPrompt')) || undefined;
          if (!ref) return;
        }
        this.referralService.payStatement(st.id, ref).subscribe({
          next: () => this.reload(),
          error: (err) => {
            this.toastError(err);
            this.reload();
          },
        });
        break;
      }
      case 'hold':
      case 'release':
        this.referralService.holdStatement(st.id, e.data.action.key === 'hold').subscribe({
          next: () => this.reload(),
          error: (err) => this.toastError(err),
        });
        break;
      case 'send':
        this.referralService.resendStatement(st.id).subscribe({
          next: () => this.toastr.success(this.translate.instant('pages.admin.referrals.statements.sent'), ''),
          error: (err) => this.toastError(err),
        });
        break;
    }
  }

  private download(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  }

  newPartner() {
    this.dialogService
      .open(ReferralPartnerDialogComponent, { context: {} })
      .onClose.subscribe((saved) => {
        if (saved) this.reload();
      });
  }

  hasAgreements(p: ReferralPartner): boolean {
    return this.agreements.some((a) => a.partner_id === p.id) || this.statements.some((s) => s.partner_name === p.name);
  }

  async deletePartner(p: ReferralPartner) {
    const ok = await firstValueFrom(
      this.dialogService.open(ConfirmDialogComponent, {
        context: { title: 'pages.admin.referrals.deleteConfirm.title', message: 'pages.admin.referrals.deleteConfirm.message' },
      }).onClose
    );
    if (!ok) return;
    this.referralService.deletePartner(p.id).subscribe({ next: () => this.reload(), error: (e) => this.toastError(e) });
  }


  // ----------------------------------------------------------- agreements

  onAgreementEvent(e: { event: string; data: any }) {
    if (e.event === 'globalAction') {
      this.openAgreementDialog();
      return;
    }
    if (e.event !== 'rowAction') return;
    const row = e.data.row as ReferralAgreement;
    switch (e.data.action.key) {
      case 'edit':
        this.openAgreementDialog(row);
        break;
      case 'sync':
        this.referralService.syncAgreement(row.id).subscribe({
          next: (r) => {
            const d = r.data as any;
            if (d?.error) {
              this.toastError({ error: { detail: d.error } });
            } else {
              this.toastr.success(
                this.translate.instant('pages.admin.referrals.synced', {
                  created: d?.created ?? 0,
                  updated: d?.updated ?? 0,
                }),
                ''
              );
            }
            this.reload();
          },
          error: (err) => this.toastError(err),
        });
        break;
      case 'end':
        this.endAgreement(row);
        break;
    }
  }

  openAgreementDialog(agreement?: ReferralAgreement) {
    this.dialogService
      .open(ReferralAgreementDialogComponent, {
        context: { agreement, partners: this.partners, clients: this.clients },
      })
      .onClose.subscribe((saved) => {
        if (saved) this.reload();
      });
  }

  private async endAgreement(row: ReferralAgreement) {
    const ok = await firstValueFrom(
      this.dialogService.open(ConfirmDialogComponent, {
        context: {
          title: 'pages.admin.referrals.endConfirm.title',
          message: 'pages.admin.referrals.endConfirm.message',
        },
      }).onClose
    );
    if (!ok) return;
    const today = new Date().toISOString().slice(0, 10);
    this.referralService.updateAgreement(row.id, { end_date: today }).subscribe({
      next: () => this.reload(),
      error: (e) => this.toastError(e),
    });
  }

  // ---------------------------------------------------------- commissions

  onCommissionEvent(e: { event: string; data: any }) {
    if (e.event === 'globalAction') {
      this.exportCsv();
      return;
    }
    const key = e.data?.action?.key;
    const ids: string[] =
      e.event === 'multiAction' ? e.data.ids : e.event === 'rowAction' ? [e.data.id] : [];
    if (!ids.length) return;
    if (key === 'release') {
      this.referralService.release(ids).subscribe({ next: () => this.reload(), error: (err) => this.toastError(err) });
    } else if (key === 'unrelease') {
      this.referralService.unrelease(ids).subscribe({ next: () => this.reload(), error: (err) => this.toastError(err) });
    } else if (key === 'paid') {
      if (!this.payoutReference.trim()) {
        this.toastr.warning(this.translate.instant('pages.admin.referrals.referenceRequired'), '');
        return;
      }
      this.referralService.markPaid(ids, this.payoutReference.trim()).subscribe({
        next: () => {
          this.payoutReference = '';
          this.reload();
        },
        error: (err) => this.toastError(err),
      });
    }
  }

  onFilterChange() {
    this.loadCommissions();
  }

  exportCsv() {
    this.referralService
      .exportCsv({ partner_id: this.partnerFilter, status: this.statusFilter })
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `provisionen_${new Date().toISOString().slice(0, 10)}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: (err) => this.toastError(err),
      });
  }

  eur(cents: number): string {
    return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  }

  private toastError(err?: any) {
    this.toastr.danger(err?.error?.detail || this.translate.instant('pages.admin.referrals.error'), '');
  }
}
